import { CurrencyPipe, DatePipe } from '@angular/common';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Component, ElementRef, inject, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute } from '@angular/router';
import { CanDirective, CapabilitiesService, CapabilityNames, Q8Breakpoints } from 'core';
import { LoadingSpinner, TableColumnInfo, TableView, TypeOfColumn } from 'design-system';
import { catchError, forkJoin, of } from 'rxjs';
import { receiptsPaymentAmountCell } from '../../components/receipts-payment-amount-cell/receipts-payment-amount-cell';
import { receiptsPaymentSettledCell } from '../../components/receipts-payment-settled-cell/receipts-payment-settled-cell';
import {
  CashBreakdownData,
  CashBreakdownResult,
  receiptsCashBreakdownDialogComponent,
} from '../../components/dialog-components/receipts-cash-breakdown-dialog-component/receipts-cash-breakdown-dialog-component';
import {
  CheckItem,
  ChecksBreakdownData,
  ChecksBreakdownResult,
  receiptsChecksBreakdownDialogComponent,
} from '../../components/dialog-components/receipts-checks-breakdown-dialog-component/receipts-checks-breakdown-dialog-component';
import {
  ConfirmDialogData,
  receiptsConfirmDialogComponent,
} from '../../components/dialog-components/receipts-confirm-dialog-component/receipts-confirm-dialog-component';
import {
  RevenueReceiptDialogData,
  receiptsReceiptDialogComponent,
} from '../../components/dialog-components/receipts-receipt-dialog-component/receipts-receipt-dialog-component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { receipts_MOCK } from '../mocks/receipts.mock';
import {
  CustomerContact,
  CustomerDetailNotification,
  CustomerDetailService,
  CustomerNotificationHeader,
  CustomersAddContactDialogComponent,
  CustomersNotificationsBottomSheet,
} from 'customers';
import {
  Invoice,
  InvoiceStatus,
  ReceiptAdvance,
  ReceiptIncomeType,
  ReceiptStatementType,
} from '../../interfaces/receipt.model';
import { ReceiptsService } from '../../services/receipts.service';
import { ReceiptEditStore } from '../../state/receipt-edit.store';
import {
  cashBreakdownFromInput,
  cashInputFromBreakdown,
  downloadBlob,
  formatDateLabel,
  toDate,
  checkInputFromItem,
  checkItemFromInput,
  invoiceResiduo,
  invoiceResiduoDaCoprire,
  invoiceSaldata,
} from '../../utils/receipt.utils';

interface PaymentRow {
  idDocumento: string;
  dataDocumento: Date | null;
  dataScadenza: Date | null;
  descrizione: string;
  totaleFattura: number;
  importoAssegnato: number;
  importoResiduo: number;
  statoSaldo: string;
  nuovoIncassoAttivo: boolean;
  saldaRichiesto?: boolean;
}

interface StatementDocument {
  idDocumento: string;
  totaleFattura: number;
  pagamenti: {
    importoPagamento: number;
    dataPagamento: Date | null;
    metodoPagamento: string;
    importoResiduo: number;
  }[];
}

interface HistoryRow {
  idDocumento: string;
  dataDocumento: string;
  dataScadenza: string;
  descrizione: string;
  totaleFattura: number;
  dataChiusura: Date | null;
  dataSaldo: string;
  importoResiduo: number;
  situazione: string;
}

interface AdvanceRow {
  codiceCliente: string;
  descrizione: string;
  importo: number;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  [ReceiptIncomeType.Cash]: 'Contanti',
  [ReceiptIncomeType.Check]: 'Assegno',
};

@Component({
  selector: 'rev-receipts-new-customer-summary',
  imports: [
    MatBadgeModule, MatButtonModule, MatTabsModule, MatFormFieldModule, MatInputModule,
    MatDatepickerModule, MatNativeDateModule,
    CanDirective, CurrencyPipe, DatePipe, MatCardModule, MatMenuModule, MatSelectModule, LoadingSpinner, TableView,
    FormsModule
  ],
  templateUrl: './receipts-new-customer-summary.html',
  styleUrl: './receipts-new-customer-summary.scss',
  providers: [ReceiptEditStore],
})
export class ReceiptsNewCustomerSummary implements OnInit, OnDestroy {
  @ViewChild('notificationsOrigin') notificationsOrigin?: ElementRef<HTMLElement>;
  @ViewChild('notificationsPanel') notificationsPanel?: TemplateRef<unknown>;

  selectedTabIndex = 0;
  newPaymentPanelOpen = false;
  isDesktop = false;
  customerId!: string;
  customerDetail: any;
  customerContacts: CustomerContact[] = [];
  notificationJson: CustomerNotificationHeader = { Count: 0, Notifications: [] };
  customerDetailLoading = false;
  private addContactDialogRef?: MatDialogRef<CustomersAddContactDialogComponent>;
  private notificationsBottomSheetRef?: MatBottomSheetRef<CustomersNotificationsBottomSheet>;
  private notificationsOverlayRef?: OverlayRef;
  private breakpointObserver = inject(BreakpointObserver);
  private overlay = inject(Overlay);
  private readonly receiptsService = inject(ReceiptsService);
  private readonly snackBar = inject(MatSnackBar);
  readonly capabilities = inject(CapabilitiesService);
  readonly CAPS = CapabilityNames;
  readonly store = inject(ReceiptEditStore);
  selectedContact?: CustomerContact;

  constructor(
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private host: ElementRef<HTMLElement>,
    private route: ActivatedRoute,
    private customerDetailService: CustomerDetailService,
    private viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe(params => {
      const routeId = params.get('idBusinessPartner');

      if (!routeId) {
        console.error('ID cliente mancante in URL');
        return;
      }

      this.customerId = routeId;
      this.loadCustomerPage();
    });

    this.breakpointObserver
      .observe([`(min-width: ${Q8Breakpoints.desktop}px)`])
      .subscribe(result => {
        this.isDesktop = result.matches;
        if (this.isDesktop) {
          this.notificationsBottomSheetRef?.dismiss();
        } else {
          this.closeNotificationsPanel();
        }
      });
  }

  ngOnDestroy() {
    this.notificationsOverlayRef?.dispose();
  }

  // Ciclo campi anagrafica per semplificare
  readonly anagraficaFields = [
    { label: 'Codice fiscale', key: 'codiceFiscale' },
    { label: 'Codice Ateco', key: 'codiceAteco' },
    { label: 'Descrizione Ateco', key: 'descrizioneAteco' },
    { label: 'Sede legale', key: 'indirizzo' },
    { label: 'Sede operativa', key: 'sedeOperativa' },
    { label: 'Cliente dal', key: 'clienteDal' },
  ];

  accontiSpese: AdvanceRow[] = [];
  statementDocuments: StatementDocument[] = [];
  statementSummary = { rischio: 0, rischioAScadere: 0, scaduti: 0, aScadere: 0 };
  statementType: ReceiptStatementType = ReceiptStatementType.All;

  historyDateFrom: Date | null = null;
  historyDateTo: Date | null = null;
  historyPayments: HistoryRow[] = [];
  historyPaymentsTable: HistoryRow[] = [];

  readonly historyPaymentColumns: TableColumnInfo[] = [
    { columnNameDef: 'idDocumento', label: 'ID Documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataDocumento', label: 'Data documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataScadenza', label: 'Data scadenza', type: TypeOfColumn.TEXT },
    { columnNameDef: 'descrizione', label: 'Descrizione', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'totaleFattura',
      label: 'Totale fattura',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    { columnNameDef: 'dataSaldo', label: 'Data saldo', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importoResiduo',
      label: 'Importo residuo',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    { columnNameDef: 'situazione', label: 'Stato', type: TypeOfColumn.TEXT },
  ];
  readonly statementMovements = receipts_MOCK.estrattoConto.movimenti.map(movement => ({
    ...movement,
    dataDocumento: this.formatMockDate(movement.dataDocumento),
    dataScadenza: movement.dataScadenza ? this.formatMockDate(movement.dataScadenza) : '-',
  }));

  readonly statementMovementColumns: TableColumnInfo[] = [
    { columnNameDef: 'dataDocumento', label: 'Data documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'numeroDocumento', label: 'Numero documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'causale', label: 'Causale', type: TypeOfColumn.TEXT },
    { columnNameDef: 'numeroRiferimento', label: 'Numero riferimento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataScadenza', label: 'Data scadenza', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'importo',
      label: 'Importo',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    { columnNameDef: 'dareAvere', label: 'Dare/Avere', type: TypeOfColumn.TEXT },
    { columnNameDef: 'situazione', label: 'Stato', type: TypeOfColumn.TEXT },
  ];

  partiteDaSaldare: PaymentRow[] = [];
  partiteDaSaldareTable: (Omit<PaymentRow, 'dataDocumento' | 'dataScadenza'>
    & { dataDocumento: string; dataScadenza: string })[] = [];

  readonly partiteDaSaldareColumns: TableColumnInfo[] = [
    { columnNameDef: 'idDocumento', label: 'ID Documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataDocumento', label: 'Data documento', type: TypeOfColumn.TEXT },
    { columnNameDef: 'dataScadenza', label: 'Data scadenza', type: TypeOfColumn.TEXT },
    { columnNameDef: 'descrizione', label: 'Descrizione', type: TypeOfColumn.TEXT },
    {
      columnNameDef: 'totaleFattura',
      label: 'Totale Fattura',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'importoAssegnato',
      label: 'Importo Assegnato',
      type: TypeOfColumn.COMPONENT,
      component: receiptsPaymentAmountCell,
    },
    {
      columnNameDef: 'importoResiduo',
      label: 'Importo Residuo',
      type: TypeOfColumn.CURRENCY,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
    {
      columnNameDef: 'saldato',
      label: '',
      type: TypeOfColumn.COMPONENT,
      component: receiptsPaymentSettledCell,
    },
    {
      columnNameDef: 'statoSaldo',
      label: 'Stato saldo',
      type: TypeOfColumn.TEXT,
      cellClass: row => row.statoSaldo === 'Saldato'
        ? 'settlement-settled'
        : row.statoSaldo ? 'settlement-in-progress' : '',
    },
  ];

  get advanceExpensesTotal(): number {
    return this.store.totaleAcconti;
  }

  get totalIncome(): number {
    return this.store.totaleIncasso;
  }

  get assignedTotal(): number {
    return this.store.totaleAssegnato;
  }

  get availableResidual(): number {
    return this.store.residuoDisponibile;
  }

  get selectedPaymentMethod(): 'cash' | 'checks' | null {
    switch (this.store.income.incomeType) {
      case ReceiptIncomeType.Cash:
        return 'cash';
      case ReceiptIncomeType.Check:
        return 'checks';
      default:
        return null;
    }
  }

  get hasPaymentsToSettle(): boolean {
    return this.partiteDaSaldare.length > 0;
  }

  private formatMockDate(value: string): string {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
  }

  loadCustomerPage() {
    this.customerDetailLoading = true;
    this.customerDetail = {};
    this.customerContacts = [];
    this.notificationJson = { Count: 0, Notifications: [] };
    this.selectedContact = undefined;

    // Usiamo il forkJoin in modo che faccia comparire tutto assieme una volta che le chiamate sono pronte
    forkJoin({
      detail: this.customerDetailService.loadCustomerDetail(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento customer detail', err);
          //Se una delle chiamate fallisce, resistuisco un oggetto vuoto per non spaccare il layout
          return of(null);
        })
      ),
      contacts: this.customerDetailService.loadCustomerContacts(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento contatti cliente', err);
          return of([] as CustomerContact[]);
        })
      ),
      receipt: this.receiptsService.getReceipt({ idBusinessPartner: this.customerId }).pipe(
        catchError((err) => {
          console.error('Errore caricamento incasso attivo', err);
          return of(null);
        })
      ),
      invoices: this.receiptsService.getPartiteAperte(this.customerId).pipe(
        catchError((err) => {
          console.error('Errore caricamento partite aperte', err);
          return of({ items: [] as Invoice[], totalItems: 0 });
        })
      ),
    }).subscribe({
      next: (result) => {
        const detailResult = result.detail as CustomerDetailNotification | null;

        this.customerDetail = detailResult?.detail ?? {};
        this.notificationJson = detailResult?.notificationJson ?? { Count: 0, Notifications: [] };
        this.customerContacts = result.contacts;
        this.customerDetailLoading = false;

        this.store.init({
          idBusinessPartner: this.customerId,
          receipt: result.receipt,
          openInvoices: result.invoices?.items ?? [],
          acconti: this.buildAcconti(),
        });

        // Un incasso gia' aperto si riprende in registrazione, non in sola lettura.
        if (result.receipt) {
          this.selectedTabIndex = 1;
          this.newPaymentPanelOpen = true;
        }

        this.projectFromStore();
        this.loadStatement();
        this.cercaStorico();
      },
      error: () => {
        this.customerDetailLoading = false;
      }
    });
  }

  private loadCustomerContacts() {
    this.customerDetailService.loadCustomerContacts(this.customerId).subscribe({
      next: (contacts: CustomerContact[]) => {
        this.customerContacts = contacts;
      },
      error: () => {
        this.customerContacts = [];
      }
    });
  }

  openAddContactDialog() {
    this.openContactDialog('add');
  }

  openEditContactDialog(contact: CustomerContact) {
    this.openContactDialog('edit', contact);
  }

  private openContactDialog(mode: 'add' | 'edit', contact?: CustomerContact) {
    if (this.addContactDialogRef) {
      return;
    }

    this.addContactDialogRef = this.dialog.open(CustomersAddContactDialogComponent, {
      data: {
        mode,
        contact,
      },
    });
    this.addContactDialogRef.afterClosed().subscribe((result?: {
      nomeCognome?: string;
      telefono?: string;
      cellulare?: string;
      fax?: string;
      email?: string;
      ruolo?: string;
      note?: string;
      default?: boolean;
    }) => {
      this.addContactDialogRef = undefined;

      if (!result) {
        return;
      }

      const payload = {
        Nominativo: result.nomeCognome ?? '',
        Ruolo: result.ruolo ?? '',
        Telefono: result.telefono ?? '',
        Cellulare: result.cellulare ?? '',
        Fax: result.fax ?? '',
        Email: result.email ?? '',
        Note: result.note ?? '',
      };

      const request$ = mode === 'edit' && contact
        ? this.customerDetailService.updateCustomerContact(this.customerId, contact.id, payload)
        : this.customerDetailService.addCustomerContact(this.customerId, payload);

      request$.subscribe({
        next: () => {
          this.loadCustomerContacts();
        },
        error: (err) => {
          console.error(`Errore ${mode === 'edit' ? 'modifica' : 'aggiunta'} contatto`, err);
        },
      });
    });
  }

  setSelectedContact(contact: CustomerContact) {
    this.selectedContact = contact;
  }

  openSelectedContactEmail() {
    const email = this.selectedContact?.email?.trim();

    if (!email) {
      return;
    }

    window.location.href = `mailto:${email}`;
  }

  openContactPhone(contact: CustomerContact) {
    const phone = (contact.cellulare || contact.telefono || '').trim();

    if (!phone) {
      return;
    }

    window.location.href = `tel:${phone}`;
  }

  deleteSelectedContact() {
    const contactId = this.selectedContact?.id;

    if (!contactId) {
      return;
    }

    this.customerDetailService.deleteCustomerContacts(this.customerId, [contactId]).subscribe({
      next: () => {
        this.selectedContact = undefined;
        this.loadCustomerContacts();
      },
      error: (err) => {
        console.error('Errore eliminazione contatto', err);
      },
    });
  }

  onNotificationsButtonClick() {
    if (this.isDesktop) {
      this.toggleNotificationsPanel();
      return;
    }

    this.openNotificationsBottomSheet();
  }

  openNotificationsBottomSheet() {
    if (this.notificationsBottomSheetRef) {
      return;
    }

    this.notificationsBottomSheetRef = this.bottomSheet.open(CustomersNotificationsBottomSheet, {
      ariaLabel: 'Notifiche',
      maxHeight: '480px',
      data: {
        notifications: this.notificationJson.Notifications ?? [],
      },
    });
    this.notificationsBottomSheetRef.afterDismissed().subscribe(() => {
      this.notificationsBottomSheetRef = undefined;
    });
  }

  toggleNotificationsPanel() {
    if (this.notificationsOverlayRef?.hasAttached()) {
      this.closeNotificationsPanel();
      return;
    }

    this.openNotificationsPanel();
  }

  closeNotificationsPanel() {
    this.notificationsOverlayRef?.detach();
  }

  getNotificationCardClass(severity: string) {
    const normalizedSeverity = severity?.toLowerCase?.() ?? '';

    switch (normalizedSeverity) {
      case 'danger':
      case 'error':
        return 'notification-card-danger';
      case 'warning':
      case 'warn':
      case 'alert':
        return 'notification-card-warning';
      case 'success':
        return 'notification-card-success';
      case 'info':
        return 'notification-card-info';
      default:
        return 'notification-card-default';
    }
  }

  getNotificationIconClass(severity: string) {
    const normalizedSeverity = severity?.toLowerCase?.() ?? '';

    switch (normalizedSeverity) {
      case 'danger':
      case 'error':
        return 'fa-solid fa-circle-exclamation';
      case 'success':
        return 'fa-solid fa-circle-check';
      case 'warning':
      case 'warn':
      case 'alert':
        return 'fa-solid fa-triangle-exclamation';
      case 'info':
        return 'fa-solid fa-circle-info';
      default:
        return 'fa-solid fa-circle-question';
    }
  }

  getNotificationIconColorClass(severity: string) {
    const normalizedSeverity = severity?.toLowerCase?.() ?? '';

    switch (normalizedSeverity) {
      case 'danger':
      case 'error':
        return 'notification-card-tone-danger';
      case 'success':
        return 'notification-card-tone-success';
      case 'warning':
      case 'warn':
      case 'alert':
        return 'notification-card-tone-warning';
      case 'info':
        return 'notification-card-tone-info';
      default:
        return 'notification-card-tone-default';
    }
  }

  getNotificationActionLabel(item: any) {
    return item?.ToAction || 'Pianifica visita';
  }

  getAnagraficaValue(key: string): string | number {
    if (key === 'codiceAteco' && this.customerDetail?.codiceAteco) {
      return this.customerDetail.codiceAteco.codice || '-';
    }

    if (key === 'descrizioneAteco' && this.customerDetail?.codiceAteco) {
      return this.customerDetail.codiceAteco.nome || '-';
    }

    return this.customerDetail?.[key] ?? '-';
  }

  // Workaround per far funzionare scroll e focus sull'header delle mat tab
  onTabIndexChange(index: number) {
    this.selectedTabIndex = index;

    if (index !== 1) {
      this.newPaymentPanelOpen = false;
      this.setPaymentTableEditing(false);
    }

    this.scrollActiveTabIntoView();

    requestAnimationFrame(() => {
      this.scrollActiveTabIntoView();
    });
  }

  toggleNewPaymentPanel(): void {
    if (!this.hasPaymentsToSettle) {
      return;
    }

    this.newPaymentPanelOpen = !this.newPaymentPanelOpen;
    this.setPaymentTableEditing(this.newPaymentPanelOpen);
  }

  openCashBreakdown(): void {
    this.dialog
      .open<receiptsCashBreakdownDialogComponent, CashBreakdownData, CashBreakdownResult>(
        receiptsCashBreakdownDialogComponent,
        { data: cashBreakdownFromInput(this.store.income.cash) },
      )
      .afterClosed()
      .subscribe(result => {
        if (!result) {
          return;
        }

        this.store
          .setIncomeCash(cashInputFromBreakdown(result.righe))
          .subscribe(() => this.afterStoreChange());
      });
  }

  openChecksBreakdown(): void {
    const corrente = this.store.income.check;

    this.dialog
      .open<receiptsChecksBreakdownDialogComponent, ChecksBreakdownData, ChecksBreakdownResult>(
        receiptsChecksBreakdownDialogComponent,
        {
          data: {
            check: corrente ? checkItemFromInput(corrente) : undefined,
            resolveBank: (abi: string, cab: string) => this.store.lookupBank(abi, cab),
            validate: (item: CheckItem) => this.store.validateCheck(checkInputFromItem(item)),
          },
        },
      )
      .afterClosed()
      .subscribe(result => {
        const assegno = result?.assegni?.[0];

        if (!assegno) {
          return;
        }

        this.store
          .setIncomeCheck(checkInputFromItem(assegno))
          .subscribe(() => this.afterStoreChange());
      });
  }

  resetNewPayment(): void {
    const data: ConfirmDialogData = { message: "Vuoi eliminare l'incasso corrente?" };

    this.dialog
      .open<receiptsConfirmDialogComponent, ConfirmDialogData, boolean>(
        receiptsConfirmDialogComponent,
        { data },
      )
      .afterClosed()
      .subscribe(confermato => {
        if (confermato) {
          this.eseguiEliminazioneIncasso();
        }
      });
  }

  private eseguiEliminazioneIncasso(): void {
    this.store
      .delete()
      .subscribe(eliminato => {
        if (!eliminato) {
          this.afterStoreChange();
          return;
        }

        this.store.init({
          idBusinessPartner: this.customerId,
          openInvoices: this.store.invoices.map(invoice => ({ ...invoice, importoAssegnato: 0 })),
          acconti: this.buildAcconti(),
        });

        this.newPaymentPanelOpen = false;
        this.afterStoreChange();
        this.setPaymentTableEditing(false);
      });
  }

  openReceipt(): void {
    if (!this.store.canConfirm || this.store.isSaving || !this.puoConfermareIncasso) {
      return;
    }

    this.store.sendReceipt().subscribe(inviato => {
      if (!inviato) {
        this.afterStoreChange();
        return;
      }

      const receiptMock = receipts_MOCK.ricevuta;
      const assignedItems = this.partiteDaSaldareTable
        .filter(payment => payment.importoAssegnato > 0)
        .map(payment => ({
          documento: payment.idDocumento,
          descrizione: payment.statoSaldo.toLowerCase() || 'acconto',
          importo: payment.importoAssegnato,
        }));

      const data: RevenueReceiptDialogData = {
        numero: String(this.store.numeroIncasso),
        cliente: this.customerDetail?.ragioneSociale ?? '',
        metodo: this.selectedPaymentMethod === 'cash'
          ? 'Contanti'
          : this.selectedPaymentMethod === 'checks'
            ? 'Assegni'
            : '-',
        importo: this.totalIncome,
        partiteImputate: assignedItems,
        email: this.store.email ?? '',
        agente: receiptMock.agente,
        onDownloadPdf: () => this.scaricaPdf(),
      };

      this.dialog.open(receiptsReceiptDialogComponent, { data });
    });
  }

  scaricaPdf(): void {
    this.store.downloadReceiptPdf().subscribe(blob => {
      if (!blob) {
        this.afterStoreChange();
        return;
      }

      const numero = this.store.numeroIncasso;
      downloadBlob(blob, numero > 0 ? `ricevuta-incasso-${numero}.pdf` : 'ricevuta-incasso.pdf');
    });
  }

  getAssignedAmount(idDocumento: string): number {
    return this.partiteDaSaldareTable.find(payment => payment.idDocumento === idDocumento)?.importoAssegnato ?? 0;
  }

  updateAssignedAmount(idDocumento: string, event: Event): void {
    const index = this.partiteDaSaldareTable.findIndex(item => item.idDocumento === idDocumento);

    if (index < 0) {
      return;
    }

    this.store.assignAmount(index, this.readAmountInput(event));
    this.afterStoreChange();
  }

  saldaPayment(row: PaymentRow): void {
    const index = this.partiteDaSaldareTable.findIndex(
      item => item.idDocumento === row.idDocumento,
    );

    if (index < 0) {
      return;
    }

    this.store.saldaInvoice(index);
    this.afterStoreChange();
  }

  updateAdvanceExpense(account: AdvanceRow, event: Event): void {
    const importo = this.readAmountInput(event);

    const acconti: ReceiptAdvance[] = this.store.income.acconti.map(entry =>
      entry.codiceClienteErp === account.codiceCliente ? { ...entry, importo } : entry,
    );

    this.store.setAcconti(acconti);
    this.afterStoreChange();
  }

  onPaymentAmountChanged(): void {
    const rows = this.partiteDaSaldareTable;

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];
      const invoice = this.store.invoices[index];

      if (!invoice) {
        continue;
      }

      if (row.saldaRichiesto) {
        row.saldaRichiesto = false;
        this.store.saldaInvoice(index);
        continue;
      }

      if (row.importoAssegnato !== invoice.importoAssegnato) {
        this.store.assignAmount(index, row.importoAssegnato);
      }
    }

    this.afterStoreChange();
  }

  loadStatement(): void {
    this.receiptsService.getDettaglioEstrattoConto(this.customerId).subscribe({
      next: info => {
        this.statementSummary = {
          rischio: info?.rischio ?? 0,
          rischioAScadere: info?.rischioAScadere ?? 0,
          scaduti: info?.scaduto ?? 0,
          aScadere: info?.aScadere ?? 0,
        };
      },
      error: err => console.error('Errore caricamento dettaglio estratto conto', err),
    });

    this.receiptsService
      .searchStatementList(this.customerId, 1, 0, { tipo: this.statementType })
      .subscribe({
        next: data => {
          this.statementDocuments = (data?.items ?? []).map(partita => ({
            idDocumento: partita.fattura,
            totaleFattura: partita.totale,
            pagamenti: (partita.incassi ?? []).map(incasso => ({
              importoPagamento: incasso.pagamento,
              dataPagamento: toDate(incasso.dataIncasso),
              metodoPagamento: PAYMENT_METHOD_LABELS[incasso.metodo] ?? '-',
              importoResiduo: incasso.redisuo,
            })),
          }));
        },
        error: err => {
          console.error('Errore caricamento estratto conto', err);
          this.statementDocuments = [];
        },
      });
  }

  cercaStorico(): void {
    this.receiptsService
      .getPartiteChiuse(this.customerId, 1, 0, this.historyDateFrom, this.historyDateTo)
      .subscribe({
        next: data => {
          this.historyPayments = (data?.items ?? []).map(invoice => this.toHistoryRow(invoice));
          this.historyPaymentsTable = this.historyPayments;
        },
        error: err => {
          console.error('Errore caricamento storico partite', err);
          this.historyPayments = [];
          this.historyPaymentsTable = [];
        },
      });
  }

  private toHistoryRow(invoice: Invoice): HistoryRow {
    return {
      idDocumento: invoice.idDocumento,
      dataDocumento: formatDateLabel(invoice.dataEmissione),
      dataScadenza: formatDateLabel(invoice.scadenza),
      descrizione: invoice.descrizione,
      totaleFattura: invoice.totale,
      dataChiusura: toDate(invoice.dataSaldo),
      dataSaldo: formatDateLabel(invoice.dataSaldo),
      importoResiduo: invoiceResiduo(invoice),
      situazione: invoice.status === InvoiceStatus.Closed ? 'Chiusa' : 'Aperta',
    };
  }

  private afterStoreChange(): void {
    this.projectFromStore();

    if (this.store.message) {
      this.snackBar.open(this.store.message, 'Chiudi', { duration: 5000 });
      this.store.clearMessage();
    }
  }

  private projectFromStore(): void {
    this.partiteDaSaldare = this.store.invoices.map(invoice => this.toPaymentRow(invoice));
    this.partiteDaSaldareTable = this.partiteDaSaldare.map(row => ({
      ...row,
      dataDocumento: formatDateLabel(row.dataDocumento),
      dataScadenza: formatDateLabel(row.dataScadenza),
    }));

    this.accontiSpese = this.store.income.acconti.map(acconto => ({
      codiceCliente: acconto.codiceClienteErp,
      descrizione: acconto.tipo === 'OIL' ? 'Acconto Oil' : 'Acconto Non Oil',
      importo: acconto.importo,
    }));
  }

  private toPaymentRow(invoice: Invoice): PaymentRow {
    return {
      idDocumento: invoice.idDocumento,
      dataDocumento: toDate(invoice.dataEmissione),
      dataScadenza: toDate(invoice.scadenza),
      descrizione: invoice.descrizione,
      totaleFattura: invoice.totale,
      importoAssegnato: invoice.importoAssegnato,
      importoResiduo: invoiceResiduoDaCoprire(invoice),
      statoSaldo: invoice.importoAssegnato > 0 ? (invoiceSaldata(invoice) ? 'Saldato' : 'Acconto') : '',
      nuovoIncassoAttivo: this.editingAbilitato,
    };
  }

  private buildAcconti(): ReceiptAdvance[] {
    const oil = this.accontiDa(this.customerDetail?.oil, 'OIL');
    const nonOil = this.accontiDa(this.customerDetail?.nonOil, 'NONOIL');

    return [...oil, ...nonOil];
  }

  private accontiDa(lista: unknown, tipo: 'OIL' | 'NONOIL'): ReceiptAdvance[] {
    if (!Array.isArray(lista)) {
      return [];
    }

    return lista.map((entry: { codice?: string }) => ({
      codiceClienteErp: entry?.codice ?? '',
      tipo,
      importo: 0,
    }));
  }

  formatAmount(value: number): string {
    return `€ ${value.toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  private readAmountInput(event: Event): number {
    const input = event.target as HTMLInputElement;
    const normalized = input.value.replace(/[^\d,.]/g, '').replace(',', '.');
    const value = Number(normalized);

    return Number.isFinite(value) && value >= 0 ? value : 0;
  }

  // Come nel mobile: si assegna solo dopo aver inserito contanti o assegno.
  get editingAbilitato(): boolean {
    return this.newPaymentPanelOpen && this.store.totaleIncasso > 0;
  }

  get puoConfermareIncasso(): boolean {
    return this.capabilities.can(CapabilityNames.incassoModifica);
  }

  private setPaymentTableEditing(active: boolean): void {
    const abilitato = active && this.store.totaleIncasso > 0;

    [...this.partiteDaSaldare, ...this.partiteDaSaldareTable].forEach(payment => {
      payment.nuovoIncassoAttivo = abilitato;
    });
  }
  scrollActiveTabIntoView() {
    const tabContainer = this.host.nativeElement.querySelector('.tabs .mat-mdc-tab-label-container') as HTMLElement | null;
    const activeTab = this.host.nativeElement.querySelector('.tabs .mat-mdc-tab.mdc-tab--active') as HTMLElement | null;

    if (!tabContainer || !activeTab) {
      return;
    }

    const containerLeft = tabContainer.scrollLeft;
    const containerRight = containerLeft + tabContainer.clientWidth;
    const tabLeft = activeTab.offsetLeft;
    const tabRight = tabLeft + activeTab.offsetWidth;

    if (tabLeft < containerLeft) {
      tabContainer.scrollLeft = tabLeft;
      return;
    }

    if (tabRight > containerRight) {
      tabContainer.scrollLeft = tabRight - tabContainer.clientWidth;
    }
  }

  openNotificationsPanel() {
    if (!this.notificationsOrigin || !this.notificationsPanel) {
      return;
    }

    if (!this.notificationsOverlayRef) {
      this.createNotificationsOverlay();
    }

    if (!this.notificationsOverlayRef?.hasAttached()) {
      this.notificationsOverlayRef?.attach(
        new TemplatePortal(this.notificationsPanel, this.viewContainerRef)
      );
    }
  }

  createNotificationsOverlay() {
    this.notificationsOverlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .global()
        .top('0')
        .right('0'),
    });

    this.notificationsOverlayRef.backdropClick().subscribe(() => {
      this.closeNotificationsPanel();
    });

    this.notificationsOverlayRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.closeNotificationsPanel();
      }
    });
  }
}
